/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Spinner,
  Badge,
  Card,
  ProgressBar,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import logo from '../assets/LogoNavbar.png';
import type {
  CompanyProfile,
  Operations,
  RestrictedFields,
  SupplierCertification,
  SupplierPolicy,
  UserOrganisation,
} from '../types/types';
import { captureSupabaseError } from '../lib/sentry';

import './profile.scss';
import { useSelector } from 'react-redux';
import {
  getBarColor,
  getTotalScorePercentage,
  sanctionScreeningStatusMap,
  tierOneSuppliersAssessmentMap,
  tierTwoSuppliersAssessmentMap,
} from '../util/util';

// const getScoreVariant = (score: number) => {
//   if (score >= 80) return 'success';
//   if (score >= 50) return 'warning';
//   return 'danger';
// };

const SupplierProfilePreview: React.FC = () => {
  const { profileId } = useParams();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [operations, setOperations] = useState<Operations | null>(null);
  const [countries, setCountries] = useState<any[]>([]);
  const [allCountries, setAllCountries] = useState<any[]>([]);
  const [policies, setPolicies] = useState<
    (SupplierPolicy & { signed_url?: string | null })[]
  >([]);
  const [certifications, setCertifications] = useState<
    (SupplierCertification & { signed_url?: string | null })[]
  >([]);
  const [restrictedFields, setRestrictedFields] =
    useState<RestrictedFields | null>(null);
  const [publishedReports, setPublishedReports] = useState<any[]>([]);
  const [industryOptions, setIndustryOptions] = useState<any[]>([]);

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const documentBucket = useMemo(() => 'supplier-profile-assets', []);

  // Helper to resolve URLs (Signed or Public)
  const resolveDocUrl = async (path: string | null | undefined) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;

    const { data, error } = await supabase.storage
      .from(documentBucket)
      .createSignedUrl(path, 60 * 15);

    if (error) {
      captureSupabaseError(error, { op: 'doc.signed-url' });
      return null;
    }
    return data?.signedUrl ?? null;
  };

  useEffect(() => {
    if (!profileId) return;

    const loadAllData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Profile
        const { data: profileData, error: profileErr } = await supabase
          .from('supplier_company_profiles')
          .select('*')
          .eq('id', profileId)
          .single();

        const isOwner =
          selectedOrganisation?.organisation?.id === profileData?.supplier_id;

        // check if i'm the owner of the profile, if not - do not show draft profiles
        if (profileData?.profile_status === 'draft' && !isOwner) {
          throw new Error('Profile not found');
        }

        if (profileErr || !profileData) throw profileErr;
        setProfile(profileData);

        const sId = profileData.supplier_id;

        // 2. Parallel Fetch Metadata & Docs
        const [
          ops,
          counts,
          allC,
          pols,
          certs,
          pubReports,
          industryOptions,
          restrictedRes,
        ] = await Promise.all([
          supabase
            .from('supplier_company_operations')
            .select('*')
            .eq('supplier_id', sId)
            .single(),
          supabase
            .from('supplier_operation_countries')
            .select('country_id, countries(name)')
            .eq('supplier_id', sId),
          supabase.from('countries').select('id, name, code'),
          supabase
            .from('supplier_policies')
            .select('*')
            .eq('supplier_id', sId)
            .eq('is_public', true)
            .order('created_at', { ascending: false }),
          supabase
            .from('supplier_certifications')
            .select('*')
            .eq('supplier_id', sId)
            .order('created_at', { ascending: false }),
          supabase
            .from('published_reports')
            .select('*')
            .eq('supplier_id', sId)
            .eq('status', 'published')
            .order('created_at', { ascending: false }),
          supabase.from('industry_options').select('*'),
          supabase
            .from('supplier_company_profile_restricted_fields')
            .select('*')
            .eq('profile_id', profileId)
            .eq('is_financials_public', true)
            .maybeSingle(),
        ]);

        if (ops.data) setOperations(ops.data);
        if (counts.data) setCountries(counts.data);
        if (allC.data) setAllCountries(allC.data);
        if (pubReports.data) setPublishedReports(pubReports.data);
        if (industryOptions.data) setIndustryOptions(industryOptions.data);
        if (restrictedRes.data) setRestrictedFields(restrictedRes.data);

        // 3. Resolve Signed URLs for both Policies and Certs
        const enrichedPolicies = await Promise.all(
          (pols.data || []).map(async (p) => ({
            ...p,
            signed_url: await resolveDocUrl(p.file_url),
          })),
        );
        const enrichedCerts = await Promise.all(
          (certs.data || []).map(async (c) => ({
            ...c,
            signed_url: await resolveDocUrl(c.certificate_file_url),
          })),
        );

        setPolicies(enrichedPolicies);
        setCertifications(enrichedCerts);
      } catch (err) {
        console.error('Profile load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [profileId]);

  const showFinancial = Boolean(restrictedFields?.is_financials_public);
  const headquartersName =
    allCountries.find((c) => c.code === profile?.headquarters_location)?.name ||
    profile?.headquarters_location ||
    '—';
  const industryLabel =
    industryOptions.find((opt) => opt.id === profile?.industry_id)?.label ||
    'Industry not specified';

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '80vh' }}
      >
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!profile)
    return (
      <Container className="py-5">
        <div className="text-center">
          <i className="bi bi-exclamation-triangle-fill text-warning display-4 mb-3"></i>
          <h3 className="mb-2">Profile Not Found</h3>
          <p className="text-muted mb-0">
            The requested supplier profile is not available. It may not exist or
            has not been published yet.
          </p>
        </div>
      </Container>
    );

  return (
    <div className="profile-shell">
      {/* Preview Header */}
      <div className="profile-topbar text-white py-2 mb-4">
        <Container className="d-flex justify-content-between align-items-center">
          <small className="opacity-75">
            <i className="bi bi-eye-fill me-2"></i>Preview Mode
          </small>
          <Badge bg="primary" className="rounded-pill px-3">
            Public Profile
          </Badge>
        </Container>
      </div>

      <Container className="profile-page">
        <Card className="profile-hero-card mb-4">
          <div className="profile-hero-header p-4 p-md-5">
            <Row className="align-items-center g-4">
              <Col md="auto" className="text-center">
                <div className="bg-white border rounded-4 p-2 d-inline-block shadow-sm">
                  {profile.logo_url ? (
                    <img
                      src={profile.logo_url}
                      alt="Logo"
                      style={{ width: 96, height: 96, objectFit: 'contain' }}
                    />
                  ) : (
                    <div
                      className="bg-light rounded d-flex align-items-center justify-content-center"
                      style={{ width: 96, height: 96 }}
                    >
                      <i className="bi bi-building text-secondary fs-2"></i>
                    </div>
                  )}
                </div>
              </Col>
              <Col>
                <div className="profile-eyebrow mb-3">
                  Preview Supplier Profile
                </div>
                <img
                  src={logo}
                  alt="SupplyPassport Logo"
                  height="52"
                  className="mb-4"
                />
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                  {profile.profile_status === 'published' && (
                    <Badge bg="success" className="rounded-pill px-3">
                      Published
                    </Badge>
                  )}
                </div>
                <h2 className="fw-bold mb-1">{profile.company_name}</h2>
                <div
                  className="mb-3"
                  style={{ color: 'rgba(241,245,249,0.82)' }}
                >
                  {industryLabel}
                </div>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <span className="profile-meta-badge">
                    <i className="bi bi-geo-alt"></i>
                    {headquartersName}
                  </span>
                  <span className="profile-meta-badge">
                    <i className="bi bi-diagram-3"></i>
                    {countries.length} active market
                    {countries.length === 1 ? '' : 's'}
                  </span>
                  <span className="profile-meta-badge">
                    <i className="bi bi-clipboard-data"></i>
                    {publishedReports.length} published report
                    {publishedReports.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div
                  className="d-flex flex-wrap gap-3 small"
                  style={{ color: 'rgba(241,245,249,0.82)' }}
                >
                  {profile.website && (
                    <a
                      href={profile.website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-decoration-none"
                      style={{ color: 'rgba(241,245,249,0.82)' }}
                    >
                      <i className="bi bi-globe me-1"></i>
                      {profile.website.replace(/(^\w+:|^)\/\//, '')}
                    </a>
                  )}
                  {profile.contact_email && (
                    <span>
                      <i className="bi bi-envelope me-1"></i>
                      {profile.contact_email}
                    </span>
                  )}
                </div>
              </Col>
              <Col md="auto" className="text-md-end">
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-light rounded-pill px-3"
                  >
                    <i className="bi bi-linkedin me-2"></i>LinkedIn
                  </a>
                )}
              </Col>
            </Row>
          </div>
          <Card.Body className="p-4 p-md-5">
            <Row className="g-3">
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">Headquarters</div>
                    <div className="profile-metric-value">
                      {headquartersName}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">Founded</div>
                    <div className="profile-metric-value">
                      {profile.founded_year || '—'}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">Employees</div>
                    <div className="profile-metric-value">
                      {operations?.employee_count || 'N/A'}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="profile-metric-card">
                  <Card.Body>
                    <div className="profile-metric-label">Revenue Range</div>
                    <div className="profile-metric-value">
                      {operations?.revenue_range || 'N/A'}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Row>
          {/* LEFT: Main Content */}
          <Col lg={8}>
            <section className="mb-4">
              <div className="profile-section-label">About</div>
              <Card className="profile-section-card">
                <div className="profile-section-head">
                  <h4 className="mb-0">Company Overview</h4>
                </div>
                <Card.Body>
                  <p
                    className="text-secondary mb-0 leading-relaxed"
                    style={{ whiteSpace: 'pre-line' }}
                  >
                    {profile.description ||
                      'The company has not provided a description yet.'}
                  </p>
                </Card.Body>
              </Card>
            </section>

            {profile.display_assesment_results && (
              <section className="mb-4">
                <div className="profile-section-label">
                  Company Compliance Assessments
                </div>
                <Card className="profile-section-card overflow-hidden">
                  <div className="profile-section-head">
                    <h4 className="mb-0">Published Assessment Reports</h4>
                  </div>
                  {publishedReports.length === 0 ? (
                    <Card.Body className="text-center py-4 text-muted small">
                      No published compliance reports available.
                    </Card.Body>
                  ) : (
                    <div className="list-group list-group-flush profile-list-group">
                      {publishedReports.map((report) => {
                        const meta = report.report_data?.meta;
                        const score = getTotalScorePercentage(
                          meta?.total_score,
                          meta?.max_score,
                        );
                        return (
                          <div key={report.id} className="list-group-item p-4">
                            <Row className="align-items-center">
                              <Col md={6}>
                                <div className="fw-bold text-dark mb-1">
                                  {meta?.template_name || 'Assessment Report'}
                                </div>
                                <div className="text-muted x-small">
                                  Published on:{' '}
                                  {new Date(
                                    report.published_at,
                                  ).toLocaleDateString()}
                                </div>
                              </Col>
                              <Col md={3} className="text-center">
                                <Badge
                                  ref={(el) =>
                                    el?.style.setProperty(
                                      'background-color',
                                      getBarColor(score),
                                      'important',
                                    )
                                  }
                                  className="rounded-pill px-3 mb-1"
                                >
                                  {score}% Overall
                                </Badge>
                                <ProgressBar
                                  now={score}
                                  ref={(el) =>
                                    el?.style.setProperty(
                                      'background-color',
                                      getBarColor(score),
                                      'important',
                                    )
                                  }
                                  style={{
                                    height: '4px',
                                  }}
                                  className="mt-1"
                                />
                              </Col>
                              <Col md={3} className="text-end mt-3 mt-md-0">
                                <div className="d-grid d-md-block">
                                  <Link
                                    to={`/assessmentResult/${report.assessment_id}`}
                                    className="btn btn-sm btn-outline-primary rounded-pill"
                                  >
                                    View Report{' '}
                                    <i className="bi bi-arrow-right ms-1"></i>
                                  </Link>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </section>
            )}

            {showFinancial && (
              <section className="mb-4">
                <div className="profile-section-label">
                  Financial & Identity
                </div>

                <Card className="profile-section-card overflow-hidden mb-3">
                  <div className="profile-section-head">
                    <h4 className="mb-0">Financial Disclosure</h4>
                  </div>
                  <Card.Body>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">UBO Details</span>
                      <span className="fw-semibold">
                        {restrictedFields?.ubo_details || '—'}
                      </span>
                    </div>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">
                        Sanction Screening
                      </span>
                      <span className="fw-semibold">
                        {sanctionScreeningStatusMap[
                          restrictedFields?.sanction_screening_status ||
                            'not_screened'
                        ]?.label || '—'}
                      </span>
                    </div>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">Credit Score</span>
                      <span className="fw-semibold">
                        {restrictedFields?.credit_score_value || '—'}
                        {' ('}
                        {restrictedFields?.credit_score_provider || '—'}
                        {')'}
                      </span>
                    </div>
                    <div className="profile-kv-row">
                      <span className="profile-kv-label">Last Audit Date</span>
                      <span className="fw-semibold">
                        {restrictedFields?.last_financial_audit_date
                          ? new Date(
                              restrictedFields.last_financial_audit_date,
                            ).toLocaleDateString()
                          : '—'}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </section>
            )}

            <section className="mb-4">
              <div className="profile-section-label">
                Supply Chain Transparency
              </div>

              <Card className="profile-section-card overflow-hidden">
                <div className="profile-section-head">
                  <h4 className="mb-0">Supply Chain Visibility</h4>
                </div>
                <Card.Body>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Tier 1 Visibility</span>
                    <span className="fw-semibold">
                      {tierOneSuppliersAssessmentMap[
                        profile.tier_1_visibility_level || 'unknown'
                      ]?.label
                        ? `${
                            tierOneSuppliersAssessmentMap[
                              profile.tier_1_visibility_level || 'unknown'
                            ]?.label
                          }`
                        : ''}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Tier 2 Visibility</span>
                    <span className="fw-semibold">
                      {tierTwoSuppliersAssessmentMap[
                        profile.tier_2_visibility_level || 'none'
                      ]?.label
                        ? `${
                            tierTwoSuppliersAssessmentMap[
                              profile.tier_2_visibility_level || 'none'
                            ]?.label
                          }`
                        : ''}
                    </span>
                  </div>
                  <div className="pt-3">
                    <div className="profile-kv-label mb-2">
                      Raw Material Origins
                    </div>
                    <div className="d-flex flex-wrap gap-1">
                      {profile.raw_material_origins?.length ? (
                        profile.raw_material_origins.map((code) => {
                          const cntry = allCountries.find(
                            (c) => c.code === code,
                          );
                          return (
                            <Badge
                              key={cntry.id}
                              bg="white"
                              text="dark"
                              className="profile-chip"
                            >
                              {cntry.name || code}
                            </Badge>
                          );
                        })
                      ) : (
                        <span className="text-muted x-small">—</span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </section>

            {/* CERTIFICATIONS (Unified design with Policies) */}
            <section className="mb-4">
              <div className="profile-section-label">
                Certifications & Accreditations
              </div>
              <Card className="profile-section-card overflow-hidden">
                <div className="profile-section-head">
                  <h4 className="mb-0">Certificates</h4>
                </div>
                {certifications.length === 0 ? (
                  <Card.Body className="text-muted small py-4 text-center">
                    No certifications found.
                  </Card.Body>
                ) : (
                  <div className="list-group list-group-flush profile-list-group">
                    {certifications.map((cert) => (
                      <div
                        key={cert.id}
                        className="list-group-item d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3"
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="icon-square bg-success-subtle text-success">
                            <i className="bi bi-patch-check-fill fs-5"></i>
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {cert.certificate_name}
                            </div>
                            <div className="text-muted x-small">
                              {cert.issuing_body} •{' '}
                              {cert.valid_until
                                ? `Expires ${cert.valid_until}`
                                : 'Permanent'}
                            </div>
                          </div>
                        </div>
                        {cert.signed_url && (
                          <a
                            href={cert.signed_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-light border px-3 rounded-pill mt-1 mt-sm-0"
                          >
                            View Doc{' '}
                            <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </section>

            {/* POLICIES */}
            <section className="mb-4">
              <div className="profile-section-label">
                Public Compliance Policies
              </div>
              <Card className="profile-section-card overflow-hidden">
                <div className="profile-section-head">
                  <h4 className="mb-0">Policy Library</h4>
                </div>
                {policies.length === 0 ? (
                  <Card.Body className="text-muted small py-4 text-center">
                    No public policies available.
                  </Card.Body>
                ) : (
                  <div className="list-group list-group-flush profile-list-group">
                    {policies.map((policy) => (
                      <div
                        key={policy.id}
                        className="list-group-item d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between gap-3"
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="icon-square bg-primary-subtle text-primary">
                            <i className="bi bi-file-earmark-text-fill fs-5"></i>
                          </div>
                          <span className="fw-bold text-dark">
                            {policy.policy_name || policy.policy_type}
                          </span>
                        </div>
                        {policy.signed_url && (
                          <a
                            href={policy.signed_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-light border px-3 rounded-pill mt-1 mt-sm-0"
                          >
                            View Policy{' '}
                            <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </section>

            {/* {restrictedFields && (
              <>
                <section className="mb-4">
                  <h6 className="text-uppercase text-muted fw-bold mb-3 small tracking-wider">
                    Financial & Identity
                  </h6>

                  <Card className="profile-card overflow-hidden mb-3">
                    <div className="list-group list-group-flush">
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">UBO Details</span>
                        <span className="fw-semibold">
                          {restrictedFields.ubo_details || '—'}
                        </span>
                      </div>
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Sanction Screening</span>
                        <span className="fw-semibold">
                          {restrictedFields.sanction_screening_status || '—'}
                        </span>
                      </div>
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Credit Score</span>
                        <span className="fw-semibold">
                          {restrictedFields.credit_score_value || '—'}
                        </span>
                      </div>
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Score Provider</span>
                        <span className="fw-semibold">
                          {restrictedFields.credit_score_provider || '—'}
                        </span>
                      </div>
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Credit Report</span>
                        {restrictedFields.credit_report_url ? (
                          <a
                            href={restrictedFields.credit_report_url}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-light border px-3 rounded-pill"
                          >
                            View Report{' '}
                            <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                          </a>
                        ) : (
                          <span className="fw-semibold">—</span>
                        )}
                      </div>
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Last Audit Date</span>
                        <span className="fw-semibold">
                          {restrictedFields.last_financial_audit_date
                            ? new Date(
                                restrictedFields.last_financial_audit_date
                              ).toLocaleDateString()
                            : '—'}
                        </span>
                      </div>
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Annual Revenue Range</span>
                        <span className="fw-semibold">
                          {restrictedFields.annual_revenue_range || '—'}
                        </span>
                      </div>
                    </div>
                  </Card>
                </section>

                <section>
                  <h6 className="text-uppercase text-muted fw-bold mb-3 small tracking-wider">
                    Supply Chain Transparency
                  </h6>

                  <Card className="profile-card overflow-hidden">
                    <Card.Header className="bg-white">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-diagram-3-fill text-primary"></i>
                        <span className="fw-bold">
                          Supply Chain Transparency
                        </span>
                      </div>
                    </Card.Header>
                    <div className="list-group list-group-flush">
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Tier 1 Visibility</span>
                        <span className="fw-semibold">
                          {restrictedFields.tier_1_visibility_level || '—'}
                        </span>
                      </div>
                      <div className="list-group-item d-flex align-items-center justify-content-between">
                        <span className="text-muted">Tier 2 Visibility</span>
                        <span className="fw-semibold">
                          {restrictedFields.tier_2_visibility_level || '—'}
                        </span>
                      </div>
                      <div className="list-group-item">
                        <div className="text-muted mb-2">
                          Raw Material Origins
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          {restrictedFields.raw_material_origins?.length ? (
                            restrictedFields.raw_material_origins.map(
                              (code) => (
                                <Badge
                                  key={code}
                                  bg="white"
                                  text="dark"
                                  className="border fw-normal px-2 py-1"
                                >
                                  {code}
                                </Badge>
                              )
                            )
                          ) : (
                            <span className="text-muted x-small">—</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                </section>
              </>
            )} */}
          </Col>

          {/* RIGHT: Sidebar */}
          <Col lg={4}>
            <section className="mb-4">
              <div className="profile-section-label">Company Snapshot</div>
              <Card className="profile-section-card">
                <div className="profile-section-head">
                  <h4 className="mb-0">Core Details</h4>
                </div>
                <Card.Body>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Headquarters</span>
                    <span className="fw-semibold">{headquartersName}</span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Founded</span>
                    <span className="fw-semibold">
                      {profile.founded_year || '—'}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Registration</span>
                    <span className="fw-semibold">
                      {profile.registration_number || '—'}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">D-U-N-S</span>
                    <span className="fw-semibold">
                      {profile.duns_number || '—'}
                    </span>
                  </div>
                  <div className="profile-kv-row">
                    <span className="profile-kv-label">Website</span>
                    {profile.website ? (
                      <a
                        href={profile.website}
                        target="_blank"
                        rel="noreferrer"
                        className="fw-semibold text-decoration-none"
                      >
                        Visit
                        <i className="bi bi-box-arrow-up-right ms-1 small"></i>
                      </a>
                    ) : (
                      <span className="fw-semibold">—</span>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </section>

            <section className="mb-4">
              <div className="profile-section-label">Operational Insights</div>
              <Card className="profile-section-card">
                <div className="profile-section-head">
                  <h4 className="mb-0">Operations Snapshot</h4>
                </div>
                <Card.Body>
                  {profile.industry_id && (
                    <div className="mb-4">
                      <label className="profile-metric-label d-block mb-1">
                        Industry
                      </label>
                      <div className="d-flex align-items-center gap-2 fw-bold">
                        {industryLabel}
                      </div>
                    </div>
                  )}
                  <div className="mb-4">
                    <label className="profile-metric-label d-block mb-1">
                      Company Size
                    </label>
                    <div className="d-flex align-items-center gap-2 fw-bold">
                      <i className="bi bi-people text-primary"></i>
                      {operations?.employee_count || 'N/A'} Employees
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="profile-metric-label d-block mb-1">
                      Revenue Range
                    </label>
                    <div className="d-flex align-items-center gap-2 fw-bold">
                      <i className="bi bi-graph-up-arrow text-primary"></i>
                      {operations?.revenue_range || 'N/A'}
                    </div>
                  </div>
                  <hr className="my-4 opacity-50" />
                  <div>
                    <label className="profile-metric-label d-block mb-2">
                      Active Markets
                    </label>
                    <div className="d-flex flex-wrap gap-1">
                      {countries.length > 0 ? (
                        countries.map((c, i) => (
                          <Badge
                            key={i}
                            bg="white"
                            text="dark"
                            className="profile-chip"
                          >
                            {c.countries?.name || c.country_id}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted x-small">
                          No markets specified
                        </span>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </section>
          </Col>
        </Row>
        <p
          className="text-center text-muted mt-4 mb-0"
          style={{ fontSize: '0.75rem' }}
        >
          This profile is generated based on self-reported data provided by the
          assessed entity. SupplyPassport has not independently verified or
          audited this information and assumes no responsibility for its
          accuracy or completeness. For more information, visit{' '}
          <a href="https://www.supplypassport.co">www.supplypassport.co</a>
        </p>
      </Container>
    </div>
  );
};

export default SupplierProfilePreview;
